import React, { useState, useRef, useEffect, useMemo } from 'react';
import {Checkbox, FormControl, FormControlLabel, RadioGroup, Radio, Select, FormGroup, Grid, MenuItem, Box} from '@mui/material';
import FormLabel from '@mui/material/FormLabel';

import CustomTextField from './../CustomTextField';
import { formatAttributeName } from '../../utils/formatting';
import {
  buildBranchScopeByParentValueMap,
  pruneDescendantFilterEntries,
} from '../../utils/categoryAttributeFilterUtils';

const controlledMethod = Object.freeze({
    valueFrom: 1,
    valueTo: 2,
    value: 3,
});
const controlledType = Object.freeze({
    decimal: 1,
    date: 2,
    text: 3
});
export default function CategoryAttribute({categoryAttributesList = null, initialValues = null, multipleColumn = false, useTypeGet = true, onCatAttrChange}) {
  const categoryAttributeDefinitions =
    categoryAttributesList?.categoryAttributesDTO ?? [];
  const branchScopeByParentValue = useMemo(
    () => buildBranchScopeByParentValueMap(categoryAttributeDefinitions),
    [categoryAttributeDefinitions]
  );
  // Initialize state from initialValues if provided (for state restoration)
  const [categoryAttributeValues, setCategoryAttributeValue] = useState(() => {
    // If initialValues is provided (restored state), use it
    if (initialValues && Array.isArray(initialValues) && initialValues.length > 0) {
      return initialValues;
    }
    return [];
  });
  const gridItemSize = multipleColumn ? {xs: 6, sm: 4, md: 3, lg: 2} : {xs: 12};
  const isFirstRender = useRef(true);
  const previousInitialValuesRef = useRef(initialValues);
  const suppressNextEmitRef = useRef(false);
  const lastEmittedStateRef = useRef(null);

  const isValueSelected = (categoryAttributeId, categoryAttributeValueId) => {
    const categoryAttribute = categoryAttributeValues.find(
      (element) =>
        Number(element.categoryAttributeId) === Number(categoryAttributeId)
    );
    if (!categoryAttribute?.itemAttributeValues) {
      return false;
    }
    return categoryAttribute.itemAttributeValues.some(
      (item) => Number(item) === Number(categoryAttributeValueId)
    );
  };

  const isAttributeSelected = (categoryAttributeId) => {
   const isFound =categoryAttributeValues?.findIndex(
     (element) =>
       parseInt(element.categoryAttributeId) === parseInt(categoryAttributeId) &&
       (element.itemAttributeValues != null && Array.isArray(element.itemAttributeValues) && element.itemAttributeValues.length > 0 || element.userAttributeValues !== null)
   ) === -1 ? false : true;
   return isFound;
  };
    // Reset all filters when categoryAttributesList change (when category changes)
    // But don't reset if we have initialValues (state restoration)
    useEffect(() => {
      if (isFirstRender.current) {
        isFirstRender.current = false;
        return;
      }

      const hasInitialValues =
        Array.isArray(initialValues) && initialValues.length > 0;
      if (hasInitialValues) {
        return;
      }

      if (!initialValues || (Array.isArray(initialValues) && initialValues.length === 0)) {
        suppressNextEmitRef.current = true;
        setCategoryAttributeValue([]);
      }
    }, [categoryAttributesList, initialValues]);
  
    // Restore state when initialValues changes (state restoration)
    useEffect(() => {
      // Skip if initialValues hasn't changed
      if (initialValues === previousInitialValuesRef.current) {
        return;
      }
      
      // If initialValues is provided and is different, restore the state
      if (initialValues && Array.isArray(initialValues) && initialValues.length > 0) {
        suppressNextEmitRef.current = true;
        setCategoryAttributeValue(initialValues);
        previousInitialValuesRef.current = initialValues;
      } else if (initialValues === null || (Array.isArray(initialValues) && initialValues.length === 0)) {
        // Clear state if initialValues is cleared (but only if it was previously set)
        if (previousInitialValuesRef.current && previousInitialValuesRef.current.length > 0) {
          suppressNextEmitRef.current = true;
          setCategoryAttributeValue([]);
        }
        previousInitialValuesRef.current = initialValues;
      }
    }, [initialValues]);
  
   // Debounce effect for category attribute changes
    useEffect(() => {
      if (isFirstRender.current) {
        isFirstRender.current = false;
        return;
      }
      /*if (suppressNextEmitRef.current) {
        suppressNextEmitRef.current = false;
        return;
      }*/

      const serializedValues = JSON.stringify(categoryAttributeValues);
      if (serializedValues === lastEmittedStateRef.current) {
        return;
      }
      lastEmittedStateRef.current = serializedValues;
      if (onCatAttrChange) {
        onCatAttrChange(categoryAttributeValues);
      }
       /*// Clear any existing timer
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
      
      // Set a new timer to call onCatAttrChange after 500ms
    debounceTimerRef.current = setTimeout(() => {
        if (onCatAttrChange) {
          onCatAttrChange(categoryAttributeValues);
        }
      }, 500); // 500ms debounce time
  
      // Cleanup function to clear the timer if the component unmounts
      return () => {
        if (debounceTimerRef.current) {
          clearTimeout(debounceTimerRef.current);
        }
      };*/
    }, [categoryAttributeValues, onCatAttrChange]);
    const handleCategoryAttributeChange = (multiple = false, predefined = true, categoryAttributeValue, categoryAttributeId, type = controlledType.text, method = controlledMethod.value) => {
    const newCategoryAttributeValue = {
      catAttributeValueId: predefined ? parseFloat(categoryAttributeValue) || -1 : -1,
      catUserAttributeValue: !predefined && categoryAttributeValue !== null && categoryAttributeValue !== "" ? categoryAttributeValue : null,
    };

    const removeEmptyParentEntry = (entries, parentAttributeId) => {
      const parentEntry = entries.find(
        (entry) => Number(entry.categoryAttributeId) === Number(parentAttributeId)
      );
      const hasPredefinedValues =
        Array.isArray(parentEntry?.itemAttributeValues) &&
        parentEntry.itemAttributeValues.length > 0;
      const hasUserValues =
        parentEntry?.userAttributeValues &&
        Object.values(parentEntry.userAttributeValues).some(
          (val) => val !== null && val !== "" && val !== undefined
        );
      if (hasPredefinedValues || hasUserValues) {
        return entries;
      }
      return entries.filter(
        (entry) => Number(entry.categoryAttributeId) !== Number(parentAttributeId)
      );
    };

    setCategoryAttributeValue((prevItems) => {
      const catAttrIndex = prevItems.findIndex(
        (item) => Number(item.categoryAttributeId) === Number(categoryAttributeId)
      );

      if (catAttrIndex === -1) {
        if (predefined && newCategoryAttributeValue.catAttributeValueId === -1) {
          return prevItems;
        }
        return [
          ...prevItems,
          {
            categoryAttributeId: categoryAttributeId,
            itemAttributeValues: predefined ? [newCategoryAttributeValue.catAttributeValueId] : null,
            userAttributeValues: !predefined ? {
              valueFrom: method === controlledMethod.valueFrom ? newCategoryAttributeValue.catUserAttributeValue : null,
              valueTo: method === controlledMethod.valueTo ? newCategoryAttributeValue.catUserAttributeValue : null,
              value: method === controlledMethod.value ? newCategoryAttributeValue.catUserAttributeValue : null,
              type: type
            } : null
          },
        ];
      }

      let nextEntries = prevItems.map((entry, index) =>
        index === catAttrIndex
          ? {
              ...entry,
              itemAttributeValues: Array.isArray(entry.itemAttributeValues)
                ? [...entry.itemAttributeValues]
                : entry.itemAttributeValues,
              userAttributeValues: entry.userAttributeValues
                ? { ...entry.userAttributeValues }
                : entry.userAttributeValues,
            }
          : entry
      );
      const updatedCatAttribute = nextEntries[catAttrIndex];
      const valueId = newCategoryAttributeValue.catAttributeValueId;

      if (multiple) {
        const catAttrValueIndex = updatedCatAttribute.itemAttributeValues?.findIndex(
          (valueItem) => Number(valueItem) === Number(valueId)
        ) ?? -1;

        if (catAttrValueIndex === -1) {
          updatedCatAttribute.itemAttributeValues.push(valueId);
        } else {
          updatedCatAttribute.itemAttributeValues.splice(catAttrValueIndex, 1);
          nextEntries = pruneDescendantFilterEntries(
            nextEntries,
            categoryAttributeId,
            valueId,
            branchScopeByParentValue
          );
          nextEntries = removeEmptyParentEntry(nextEntries, categoryAttributeId);
        }
        return nextEntries;
      }

      const previousValueId = updatedCatAttribute.itemAttributeValues?.[0];
      const shouldClearPreviousDescendants =
        previousValueId != null &&
        (valueId === -1 || Number(previousValueId) !== Number(valueId));

      if (shouldClearPreviousDescendants) {
        nextEntries = pruneDescendantFilterEntries(
          nextEntries,
          categoryAttributeId,
          previousValueId,
          branchScopeByParentValue
        );
      }

      if (valueId !== -1 && predefined) {
        updatedCatAttribute.itemAttributeValues = [valueId];
      } else if (valueId === -1 && predefined) {
        updatedCatAttribute.itemAttributeValues = [];
      }

      if (!predefined) {
        if (!updatedCatAttribute.userAttributeValues) {
          updatedCatAttribute.userAttributeValues = {
            valueFrom: null,
            valueTo: null,
            value: null,
            type: type,
          };
        }
        switch (method) {
          case controlledMethod.valueFrom:
            updatedCatAttribute.userAttributeValues.valueFrom = newCategoryAttributeValue.catUserAttributeValue;
            break;
          case controlledMethod.valueTo:
            updatedCatAttribute.userAttributeValues.valueTo = newCategoryAttributeValue.catUserAttributeValue;
            break;
          case controlledMethod.value:
            updatedCatAttribute.userAttributeValues.value = newCategoryAttributeValue.catUserAttributeValue;
            break;
          default:
            break;
        }
      }

      if (predefined && valueId === -1) {
        nextEntries = removeEmptyParentEntry(nextEntries, categoryAttributeId);
      }

      return nextEntries;
    });
  };
    // Render a specific filter
     const renderFilter = (categoryAttribute) => {
       // Extract attribute properties
       const type = useTypeGet ? categoryAttribute.typeGet : categoryAttribute.typeSet;
       const categoryAttributeId = categoryAttribute.id;
       const categoryAttributeValuesList = categoryAttribute.categoryAttributesValueDTO	 || [];
       const name = categoryAttribute.name;
       const jednostka = categoryAttribute.jednostka;
       const rangeMin = categoryAttribute.rangeMin;
       const rangeMax = categoryAttribute.rangeMax;
       const minLength = categoryAttribute.minLegth;
       const maxLength = categoryAttribute.maxLength;
       const isRequired = categoryAttribute.required;
       const attrEntry = categoryAttributeValues.find(
         (item) => parseInt(item.categoryAttributeId, 10) === parseInt(categoryAttributeId, 10)
       );
       const uav = attrEntry?.userAttributeValues;
   
       // Render the appropriate control based on the type
       switch (type) {
         case 'select':
           return (
             categoryAttribute.categoryAttributesValueDTO.length > 0 && <FormControl required={isRequired && !useTypeGet}  error={isRequired && !useTypeGet && !isAttributeSelected(categoryAttributeId)} fullWidth="true" name="selectFilter" size="small">
               <FormLabel component="legend">{formatAttributeName(name, jednostka)}</FormLabel>
              <Select
              sx={{minWidth: "160px"}}
                autoWidth="true" 
                value={categoryAttributeValues.find((item) => item.categoryAttributeId === categoryAttributeId)?.itemAttributeValues?.[0] || ""}
                onChange={(event) => {
                  const value = event.target.value;
                  // Convert empty string to -1 to trigger filter removal
                  handleCategoryAttributeChange(false, true, value === "" ? -1 : value, categoryAttributeId);
                }}
               >
                 <MenuItem value="">
                   <em>Brak</em>
                 </MenuItem>
                 {categoryAttributeValuesList.map((attributeValue) => {
                   return (
                     <MenuItem key={attributeValue.id} value={attributeValue.id}>
                       {attributeValue.value}
                     </MenuItem>
                   );
                 })}
               </Select>
             </FormControl>
           );
   
         case 'radio':
         case 'radiobutton':
           return (
             categoryAttribute.categoryAttributesValueDTO.length > 0 && <FormControl required={isRequired && !useTypeGet} error={isRequired && !useTypeGet && !isAttributeSelected(categoryAttributeId)} name="radioFilter">
               <FormLabel component="legend">{formatAttributeName(name, jednostka)}</FormLabel>
               <RadioGroup
                 value={categoryAttributeValues.find((item) => item.categoryAttributeId === categoryAttributeId)?.itemAttributeValues?.[0] || ""}
                 onChange={(event) => {
                   const value = event.target.value;
                   // Convert empty string to -1 to trigger filter removal
                   handleCategoryAttributeChange(false, true, value === "" ? -1 : value, categoryAttributeId);
                 }}
               >
                 {categoryAttributeValuesList.map((attributeValue) => {
                   return (
                    <Box>
                     <Box key={attributeValue.id}>
                       <FormControlLabel
                         value={attributeValue.id.toString()}
                         control={<Radio size="small" />}
                         label={
                           <Box sx={{ display: 'flex', alignItems: 'center' }}>
                             <span>{attributeValue.value}</span>
                           </Box>
                         }
                       />
                     </Box>
                   {isValueSelected(categoryAttributeId, attributeValue.id) && attributeValue.categoryAttributesDTO  &&
                      <Box sx={{ pl: 4, mt: 1, mb: 1, borderLeft: '1px dashed rgba(0,0,0,0.1)' }}>
                        {renderFilter(attributeValue.categoryAttributesDTO)}
                      </Box>
                      }
                      </Box>
                   );
                 })}
               </RadioGroup>
             </FormControl>
           )
   
         case 'checkbox':
           return(
             categoryAttribute.categoryAttributesValueDTO.length > 0 && <FormGroup required={isRequired && !useTypeGet} error={isRequired && !useTypeGet && !isAttributeSelected(categoryAttributeId)} name="checkboxFilter">
               <FormLabel component="legend">{formatAttributeName(name, jednostka)}</FormLabel>
               {categoryAttributeValuesList.map((attributeValue) => {
                 return (
                  <Box>
                   <Box key={attributeValue.id}>
                     <FormControlLabel
                       control={<Checkbox
                         onChange={(event) => handleCategoryAttributeChange(true, true, event.target.value, categoryAttributeId)}
                         size='small'
                         name={attributeValue.value}
                         value={attributeValue.id}
                         checked={isValueSelected(categoryAttributeId, attributeValue.id)}
                       />}
                       label={
                         <Box sx={{ display: 'flex', alignItems: 'center' }}>
                           <span>{attributeValue.value}</span>
                           {attributeValue.categoryAttributesDTO && <span style={{ marginLeft: '4px', fontSize: '0.8rem', color: 'text.secondary' }}>▼</span>
                           }
                         </Box>
                       }
                     />
                   </Box>
                   {isValueSelected(categoryAttributeId, attributeValue.id) && attributeValue.categoryAttributesDTO  &&
                      <Box sx={{ pl: 4, mt: 1, mb: 1, borderLeft: '1px dashed rgba(0,0,0,0.1)' }}>
                        {renderFilter(attributeValue.categoryAttributesDTO)}
                      </Box>
                      }
                  </Box>
                 );
               })}

             </FormGroup>
           );
   
         case 'number':
         case 'double':
          if(useTypeGet &&( rangeMin > 0 && rangeMax > 0)) {
            return(
             <FormControl required={isRequired && !useTypeGet} error={isRequired && !useTypeGet && !isAttributeSelected(categoryAttributeId)} name="textFilter">
                <FormLabel component="legend">{formatAttributeName(name, jednostka)}</FormLabel>
                Od:
               <CustomTextField
                 type={type}
                 rangeMin={rangeMin}
                 rangeMax={rangeMax}
                 lengthMin={minLength}
                 lengthMax={maxLength}
                 value={uav?.valueFrom ?? ""}
                 onChange={(newValue) => handleCategoryAttributeChange(false, false, newValue, categoryAttributeId, controlledType.decimal, controlledMethod.valueFrom)}
                 slotProps={{ inputLabel: { shrink: true }}}
               />
               Do:
               <CustomTextField
                 type={type}
                 rangeMin={rangeMin}
                 rangeMax={rangeMax}
                 lengthMin={minLength}
                 lengthMax={maxLength}
                 value={uav?.valueTo ?? ""}
                 onChange={(newValue) => handleCategoryAttributeChange(false, false, newValue, categoryAttributeId, controlledType.decimal, controlledMethod.valueTo)}
                 slotProps={{ inputLabel: { shrink: true }}}
               />
             </FormControl>
           );
          } else {
            return(
             <FormControl required={isRequired && !useTypeGet} error={isRequired && !useTypeGet && !isAttributeSelected(categoryAttributeId)} name="textFilter">
                <FormLabel component="legend">{formatAttributeName(name, jednostka)}</FormLabel>
               <CustomTextField
                 type={type}
                 rangeMin={rangeMin}
                 rangeMax={rangeMax}
                 lengthMin={minLength}
                 lengthMax={maxLength}
                 value={uav?.value ?? ""}
                 onChange={(newValue) => handleCategoryAttributeChange(false, false, newValue, categoryAttributeId, controlledType.decimal, controlledMethod.value)}
                 slotProps={{ inputLabel: { shrink: true }}}
               />
             </FormControl>
            );
          }
          case 'text':
            return(
             <FormControl required={isRequired && !useTypeGet} error={isRequired && !useTypeGet && !isAttributeSelected(categoryAttributeId)} name="textFilter">
                <FormLabel component="legend">{formatAttributeName(name, jednostka)}</FormLabel>
               <CustomTextField
                 type={type}
                 rangeMin={rangeMin}
                 rangeMax={rangeMax}
                 lengthMin={minLength}
                 lengthMax={maxLength}
                 value={uav?.value ?? ""}
                 onChange={(newValue) => handleCategoryAttributeChange(false, false, newValue, categoryAttributeId, controlledType.text, controlledMethod.value)}
                 slotProps={{ inputLabel: { shrink: true }}}
               />
             </FormControl>
           );
         default:
           return null;
       }
     };
   

  return (
<Box>
  <Grid container spacing={1} justifyContent= "flex-start">
    {categoryAttributeDefinitions.map((categoryAttribute) => {
            return <Grid item xs={gridItemSize.xs} sm={gridItemSize.sm} md={gridItemSize.md} lg={gridItemSize.lg} key={categoryAttribute.id}>
                      {renderFilter(categoryAttribute)}
              </Grid>
            })}
  </Grid>
</Box> 
  )
}

